<script lang="ts">
	import TextImageSidesStructure, {
		ItemDescription,
		ItemNativeName
	} from '$lib/components/city/features/TextImageSidesStructure.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import KeepExploringSection from '$lib/components/ui/section/KeepExploringSection.svelte';
	import { YEAR_SEASON_COLOURS, YEAR_SEASON_ICONS, YearSeason } from '$lib/features/_other/yearSeason.enum';
	import { CITY_FEATURE_TYPE_COLOURS, CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import { type SeasonalHighlight } from '$lib/features/seasonalHighlight/seasonalHighlight.type';
	import { FULL_SITE_URL } from '$lib/statics';
	import { findFirstAvailableImage } from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import type { Component } from 'svelte';
	import type { Attachment } from 'svelte/attachments';
	import IconThermometer from '~icons/mdi/thermometer';
	import type { Output } from './+page.server';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';

	const featureType: CityFeatureType = CityFeatureType.SEASONAL_HIGHLIGHT;
	const SEASON_CONFIG: Record<YearSeason, { text: string; bgGlow: string; icon: Component }> = {
		[YearSeason.SPRING]: {
			text: YEAR_SEASON_COLOURS[YearSeason.SPRING].text,
			bgGlow: YEAR_SEASON_COLOURS[YearSeason.SPRING].bg,
			icon: YEAR_SEASON_ICONS[YearSeason.SPRING]
		},
		[YearSeason.SUMMER]: {
			text: YEAR_SEASON_COLOURS[YearSeason.SUMMER].text,
			bgGlow: YEAR_SEASON_COLOURS[YearSeason.SUMMER].bg,
			icon: YEAR_SEASON_ICONS[YearSeason.SUMMER]
		},
		[YearSeason.AUTUMN]: {
			text: YEAR_SEASON_COLOURS[YearSeason.AUTUMN].text,
			bgGlow: YEAR_SEASON_COLOURS[YearSeason.AUTUMN].bg,
			icon: YEAR_SEASON_ICONS[YearSeason.AUTUMN]
		},
		[YearSeason.WINTER]: {
			text: YEAR_SEASON_COLOURS[YearSeason.WINTER].text,
			bgGlow: YEAR_SEASON_COLOURS[YearSeason.WINTER].bg,
			icon: YEAR_SEASON_ICONS[YearSeason.WINTER]
		}
	};

	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const items = $derived<SeasonalHighlight[]>(output.items);
	let activeSeason = $derived<YearSeason>(items[0]?.seasonName || YearSeason.SPRING);
	const meta = $derived.by(() => {
		const firstAvailableImage: CoverImage | undefined = findFirstAvailableImage(items);
		return {
			title: `${location.city.label} Summer, Winter, Spring & Autumn: Seasonal Highlights`,
			description: `Discover ${location.city.label} through the seasons — Spring, Summer, Autumn and Winter — with temperatures and highlights to help you plan the perfect visit.`,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeatures({ location, type: featureType }).href}`,
			imageUrl: firstAvailableImage ? UrlUtil.getFullImageUrl(firstAvailableImage) : undefined
		};
	});
	const combinedJsonLd = $derived(data.output.jsonLdBreadcrumbs);

	const observeSeason: Attachment = (node: Element) => {
		const seasonStr = node.getAttribute('data-season') as YearSeason;
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						activeSeason = seasonStr;
					}
				});
			},
			{
				rootMargin: '-20% 0px -40% 0px',
				threshold: 0.1
			}
		);
		observer.observe(node);
		return () => {
			observer.disconnect();
		};
	};
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />
	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	{#if meta.imageUrl}
		<meta property="og:image" content={meta.imageUrl} />
		<meta property="twitter:image" content={meta.imageUrl} />
	{/if}
	<meta property="og:type" content="website" />
	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<HeroNeutral
	title={`The Seasons of ${location.city.label}`}
	description={`Every season brings a distinct personality to the city. Whether you prefer the vibrant blooms of spring or the crisp, quiet
		charm of winter, find out what makes ${location.city.label} beautiful year-round.`}
	breadcrumbs={data.output.breadcrumbs}
	decorations={{
		hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
	}}
/>

<div class="pointer-events-none fixed inset-0 overflow-hidden">
	{@render DynamicAmbientBackgroundGlow()}
</div>

<div class="container mx-auto px-4 py-14 md:py-16 lg:px-0">
	<ul class="flex flex-col gap-32 lg:gap-48">
		{#each items as item, i (item.feature.id)}
			<li>
				{@render SeasonRow(item, i)}
			</li>
		{/each}
	</ul>

	<KeepExploringSection categories={output.keepExploring} {location} />
</div>

{#snippet DynamicAmbientBackgroundGlow()}
	{const bgGlow = $derived<string>(SEASON_CONFIG[activeSeason]?.bgGlow)}
	<div
		class="absolute top-1/4 -right-1/4 h-[85vh] w-[85vw] rounded-full opacity-22 blur-[200px] transition-colors duration-1000 ease-in-out {bgGlow}"
	></div>
{/snippet}

{#snippet SeasonRow(item: SeasonalHighlight, index: number)}
	{const config = $derived(SEASON_CONFIG[item.seasonName])}
	{const Icon = $derived<Component>(config.icon)}
	{const temp = $derived<number>(output.seasonTemps[item.seasonName])}

	<article
		class="flex scroll-mt-24 flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-16"
		id={item.feature.slug}
		data-season={item.seasonName}
		{@attach observeSeason}
	>
		<TextImageSidesStructure englishName={item.feature.englishName} images={item.feature.images} eagerLoadImages={index === 0}>
			{#snippet TextContentSnippet()}
				<div class="flex items-center justify-between pb-4">
					<h2 class="flex items-center gap-3 text-3xl text-base-content lg:text-5xl">
						<Icon class="{config.text} text-2xl lg:text-4xl" />
						{item.feature.englishName}
					</h2>

					{#if temp !== undefined}
						<span class="flex items-center gap-1 font-mono text-xl font-bold {config.text}">
							<IconThermometer class="text-base-content/30" />
							{temp}°C
						</span>
					{/if}
				</div>

				<div class="divider m-0 {config.bgGlow} h-[0.01rem] opacity-30"></div>

				{@render ItemNativeName({
					nativeName: item.feature.nativeName,
					language: output.countryLanguage
				})}
				{@render ItemDescription({ text: item.feature.description })}
			{/snippet}
		</TextImageSidesStructure>
	</article>
{/snippet}
